<template>
  <div
    v-if="structure"
    class="structure-settings-bar absolute -top-12 left-0 right-0 z-20 flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-t-lg shadow-lg"
  >
    <!-- Component Type Label -->
    <div class="flex items-center gap-2 mr-4">
      <i :class="getStructureIcon(structure.type)" class="text-sm"></i>
      <span class="font-medium text-sm">{{ getStructureName(structure.type) }}</span>
    </div>

    <!-- Layout Mode (for FlexSection) -->
    <div v-if="isFlexSection" class="flex items-center gap-2">
      <label class="text-xs opacity-80">Layout:</label>
      <Select
        v-model="localData.layout"
        :options="layoutOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Layout"
        class="w-32 p-inputtext-sm structure-select"
        @change="handlePropertyChange('layout', localData.layout)"
      />
    </div>

    <!-- Layout Preset (for LayoutGrid) -->
    <div v-if="isLayoutGrid" class="flex items-center gap-2">
      <label class="text-xs opacity-80">Layout:</label>
      <Select
        v-model="localData.layout"
        :options="gridLayoutOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Layout"
        class="w-36 p-inputtext-sm structure-select"
        @change="handlePropertyChange('layout', localData.layout)"
      />
    </div>

    <!-- Columns (for FlexSection grid) -->
    <div v-if="isFlexSection && localData.layout === 'grid'" class="flex items-center gap-2">
      <label class="text-xs opacity-80">Columns:</label>
      <Select
        v-model="localData.columns"
        :options="columnOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Auto"
        class="w-20 p-inputtext-sm structure-select"
        @change="handlePropertyChange('columns', localData.columns)"
      />
    </div>

    <!-- Gap -->
    <div class="flex items-center gap-2">
      <label class="text-xs opacity-80">Gap:</label>
      <Select
        v-model="localData.gap"
        :options="gapOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Medium"
        class="w-24 p-inputtext-sm structure-select"
        @change="handlePropertyChange('gap', localData.gap)"
      />
    </div>

    <!-- Alignment (for FlexSection) -->
    <div v-if="isFlexSection && ['flex-row', 'flex-col'].includes(localData.layout)" class="flex items-center gap-2">
      <label class="text-xs opacity-80">Align:</label>
      <Select
        v-model="localData.align"
        :options="alignOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Stretch"
        class="w-24 p-inputtext-sm structure-select"
        @change="handlePropertyChange('align', localData.align)"
      />
    </div>

    <!-- Equal Height Toggle -->
    <div class="flex items-center gap-2 ml-2">
      <Checkbox
        v-model="localData.equalHeight"
        :binary="true"
        inputId="equalHeight"
        class="structure-checkbox"
        @change="handlePropertyChange('equalHeight', localData.equalHeight)"
      />
      <label for="equalHeight" class="text-xs cursor-pointer">Equal Height</label>
    </div>

    <!-- Spacer -->
    <div class="flex-1"></div>

    <!-- Container Width -->
    <div class="flex items-center gap-2">
      <label class="text-xs opacity-80">Width:</label>
      <Select
        v-model="localData.containerWidth"
        :options="containerWidthOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Contained"
        class="w-28 p-inputtext-sm structure-select"
        @change="handlePropertyChange('containerWidth', localData.containerWidth)"
      />
    </div>

    <!-- More Options Button -->
    <Button
      icon="pi pi-cog"
      size="small"
      text
      class="text-white hover:bg-purple-500 ml-2"
      @click="$emit('openFullEditor')"
      v-tooltip="'More options'"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue'
import Select from 'primevue/select'
import Checkbox from 'primevue/checkbox'
import Button from 'primevue/button'
import type { SelectedStructure } from '@/stores/ui'

interface Props {
  structure: SelectedStructure | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  propertyChange: [property: string, value: any]
  openFullEditor: []
}>()

// Local data state for reactive controls
const localData = reactive<Record<string, any>>({
  layout: 'grid',
  columns: 3,
  gap: 'medium',
  align: 'stretch',
  justify: 'start',
  wrap: true,
  equalHeight: false,
  containerWidth: 'contained'
})

// Watch for structure changes and sync local data
watch(() => props.structure, (newStructure) => {
  if (newStructure?.data) {
    Object.assign(localData, {
      layout: newStructure.data.layout || 'grid',
      columns: newStructure.data.columns || 3,
      gap: newStructure.data.gap || 'medium',
      align: newStructure.data.align || 'stretch',
      justify: newStructure.data.justify || 'start',
      wrap: newStructure.data.wrap ?? true,
      equalHeight: newStructure.data.equalHeight ?? false,
      containerWidth: newStructure.data.containerWidth || 'contained'
    })
  }
}, { immediate: true })

// Component type checks
const isFlexSection = computed(() => props.structure?.type === 'cms.flex-section')
const isLayoutGrid = computed(() => props.structure?.type === 'cms.layout-grid')

// Options
const layoutOptions = [
  { label: 'Grid', value: 'grid' },
  { label: 'List', value: 'list' },
  { label: 'Masonry', value: 'masonry' },
  { label: 'Gallery Strip', value: 'gallery-strip' },
  { label: 'Flex Row', value: 'flex-row' },
  { label: 'Flex Column', value: 'flex-col' }
]

const gridLayoutOptions = [
  { label: '2 Columns', value: '2-col' },
  { label: '3 Columns', value: '3-col' },
  { label: '4 Columns', value: '4-col' },
  { label: 'Sidebar', value: '2-col-sidebar' },
  { label: 'Masonry', value: 'masonry' }
]

const columnOptions = [
  { label: 'Auto', value: 'auto' },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 }
]

const gapOptions = [
  { label: 'None', value: 'none' },
  { label: 'Small', value: 'small' },
  { label: 'Medium', value: 'medium' },
  { label: 'Large', value: 'large' }
]

const alignOptions = [
  { label: 'Start', value: 'start' },
  { label: 'Center', value: 'center' },
  { label: 'End', value: 'end' },
  { label: 'Stretch', value: 'stretch' }
]

const containerWidthOptions = [
  { label: 'Contained', value: 'contained' },
  { label: 'Wide', value: 'wide' },
  { label: 'Full Width', value: 'full' }
]

// Helper functions
const getStructureName = (type: string): string => {
  const nameMap: Record<string, string> = {
    'cms.flex-section': 'Flex Layout',
    'cms.layout-grid': 'Grid Layout'
  }
  return nameMap[type] || type
}

const getStructureIcon = (type: string): string => {
  const iconMap: Record<string, string> = {
    'cms.flex-section': 'pi pi-objects-column',
    'cms.layout-grid': 'pi pi-th-large'
  }
  return iconMap[type] || 'pi pi-box'
}

// Handle property changes
const handlePropertyChange = (property: string, value: any) => {
  emit('propertyChange', property, value)
}
</script>

<style scoped>
.structure-settings-bar {
  background: linear-gradient(135deg, rgb(147 51 234) 0%, rgb(124 58 237) 100%);
}

/* Custom styling for selects in dark context */
:deep(.structure-select) {
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: white !important;
}

:deep(.structure-select .p-select-label) {
  color: white !important;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

:deep(.structure-select .p-select-trigger) {
  color: white !important;
}

:deep(.structure-select:hover) {
  border-color: rgba(255, 255, 255, 0.5) !important;
}

:deep(.structure-checkbox .p-checkbox-box) {
  background: rgba(255, 255, 255, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

:deep(.structure-checkbox .p-checkbox-box.p-highlight) {
  background: white !important;
  border-color: white !important;
}

:deep(.structure-checkbox .p-checkbox-icon) {
  color: rgb(147 51 234) !important;
}
</style>
